import { getBrand, brandLogoPath } from '../../lib/brandClient.js';

export type BrandLogoVariant =
  | 'mark'
  | 'wordmark-short'
  | 'wordmark-full'
  | 'lockup'
  | 'lockup-stacked';

export interface BrandLogoProps {
  variant?: BrandLogoVariant;
  className?: string;
  /** Inverse color treatment: applies the mono-white logo on dark backgrounds. */
  inverse?: boolean;
  /** Optional ARIA label (defaults to brand name). */
  ariaLabel?: string;
}

/**
 * <BrandLogo /> — generic, brand-agnostic logo renderer.
 *
 * Reads the SVG path from /brand/brand.config.json. NEVER hard-codes file names;
 * NEVER references the brand string directly. Lockup variants are derived by
 * convention (sibling of brand.mark in /brand/logo).
 */
export function BrandLogo({
  variant = 'mark',
  className,
  inverse = false,
  ariaLabel,
}: BrandLogoProps): JSX.Element {
  const cfg = getBrand();
  const src = resolveSrc(variant, inverse);
  const alt = ariaLabel ?? cfg.name.full;
  return <img src={src} alt={alt} className={className} draggable={false} />;
}

function resolveSrc(variant: BrandLogoVariant, inverse: boolean): string {
  const cfg = getBrand();
  if (variant === 'mark') {
    return inverse ? brandLogoPath('mark-mono-white.svg') : cfg.mark;
  }
  if (variant === 'wordmark-short') return cfg.wordmark.short;
  if (variant === 'wordmark-full') return cfg.wordmark.full;
  if (variant === 'lockup') return brandLogoPath('lockup-horizontal.svg');
  return brandLogoPath('lockup-stacked.svg');
}
